import { memo, useCallback, useLayoutEffect, useRef, type MutableRefObject } from 'react'
import { useReducedMotion } from 'framer-motion'
import { MessageMarkdown } from './message-markdown'
import { REVEAL_CHUNK_CLASS } from './reveal-chunks'
import { CHUNK_REVEAL_DURATION_MS, computeChunkDelays } from './reveal-delays'
import { advanceRevealedLength, getRevealedLength } from './reveal-once'
import { continuesText } from '../../../../utils/continues-text'
import './final-reveal.css'

// Padding past the last chunk's animation end before the fallback force-
// completes the reveal (occluded windows can throttle or skip animations).
const REVEAL_FALLBACK_SLACK_MS = 250

// How much of an answer's opening identifies it. A shorter answer is too
// generic to recognise in other text, so it never counts as the same answer.
const SAME_ANSWER_OPENING_CHARS = 40

/**
 * Whether `next` is the answer `previous` was revealing, laid out again: a
 * resume grows it at the end, a refetch rebuilds the parts with the folded
 * lead-in placed before it, and the next model step carries on its sentence.
 */
function isSameAnswer(previous: string, next: string): boolean {
  const answer = previous.trim()
  const laidOut = next.trim()
  if (answer && laidOut.startsWith(answer) && continuesText(answer, laidOut.slice(answer.length))) {
    return true
  }
  if (answer.length < SAME_ANSWER_OPENING_CHARS) {
    return false
  }
  return laidOut.startsWith(answer.slice(0, SAME_ANSWER_OPENING_CHARS)) || laidOut.endsWith(answer)
}

/**
 * The reveal's units in document order: every chunk span the markdown pass
 * wrapped, plus each top-level block that contains none (code blocks, tables,
 * rules) as a single unit. Document order is what makes the monotonic-delay
 * watermark in the effect below sound.
 */
function collectRevealUnits(container: HTMLElement): Array<HTMLElement> {
  const units: Array<HTMLElement> = []
  for (const block of Array.from(container.children) as Array<HTMLElement>) {
    const chunks = block.querySelectorAll<HTMLElement>(`.${REVEAL_CHUNK_CLASS}`)
    if (chunks.length > 0) {
      units.push(...Array.from(chunks))
    } else {
      units.push(block)
    }
  }
  return units
}

function measureSharedPrefix(a: string, b: string): number {
  let length = 0
  while (length < a.length && length < b.length && a[length] === b[length]) length++
  return length
}

/** Each unit's offsets in the container's text, in document order. */
function measureUnitSpans(units: Array<HTMLElement>): Array<{ start: number; end: number }> {
  let offset = 0
  return units.map((unit) => {
    const start = offset
    offset += unit.textContent?.length ?? 0
    return { start, end: offset }
  })
}

/**
 * Elements that paint their own visuals around chunked text — list markers,
 * blockquote borders. Held hidden until their first chunk's delay so a list
 * doesn't show a column of empty bullets before any text has appeared.
 */
function collectHolders(container: HTMLElement): Array<HTMLElement> {
  const holders: Array<HTMLElement> = []
  for (const block of Array.from(container.children) as Array<HTMLElement>) {
    if (block.matches('blockquote')) holders.push(block)
    holders.push(...Array.from(block.querySelectorAll<HTMLElement>('li, blockquote')))
  }
  return holders
}

interface FinalMessageRevealProps {
  text: string
  messageId: string
  /** Watermark key, `messageId` by default. A live reveal passes one per answer segment. */
  revealKey?: string
  shouldAnimate?: boolean
  // Mutable by contract: this component assigns the rendered container into it.
  renderedContentRef?: MutableRefObject<HTMLDivElement | null>
}

/**
 * Renders the answer and reveals the text it hasn't shown yet: after the turn
 * ends by default, or as it streams in with the live reveal, where each update
 * reveals only the new text. Chunks (per-word spans, or whole non-text blocks)
 * land sequentially — each takes no space until its delay, so the message
 * grows like live streaming; no opacity fade, blur, or rise — driven entirely
 * by CSS + imperative DOM writes so no React commits happen while it plays.
 *
 * One-shot by construction: a session-scoped watermark (reveal-once.ts) keyed
 * by `revealKey` records how much of the text has been shown, so remounts
 * within a session (chat switches, stream resumes, refetch swaps) continue or
 * skip the reveal instead of replaying it.
 */
export const FinalMessageReveal = memo(function FinalMessageReveal({
  text,
  messageId,
  revealKey = messageId,
  shouldAnimate = true,
  renderedContentRef,
}: FinalMessageRevealProps) {
  const containerRef = useRef<HTMLDivElement | null>(null)
  const assignContainerRef = useCallback(
    (el: HTMLDivElement | null) => {
      containerRef.current = el
      if (renderedContentRef) {
        renderedContentRef.current = el
      }
    },
    [renderedContentRef],
  )

  const reducedMotion = useReducedMotion()
  // Characters shown by a previous pass, seeded from the session registry so a
  // remount (stream resume, refetch swap) continues instead of replaying.
  const revealedLengthRef = useRef(getRevealedLength(revealKey))
  // Latch: once a text's reveal has finished, later effect re-runs (e.g.
  // reduced-motion toggles) must not replay it.
  const completedForTextRef = useRef<string | null>(null)
  // The key and text the length above belongs to.
  const countedKeyRef = useRef(revealKey)
  const countedTextRef = useRef(text)
  // What the container rendered last pass, to tell which shown text a new
  // key leaves in place.
  const renderedTextRef = useRef('')

  useLayoutEffect(() => {
    const container = containerRef.current
    if (!text || !container) {
      return
    }
    // A new key under a mounted reveal: the same answer laid out again has
    // been shown in full already; a new answer segment starts from its own
    // watermark, but text it leaves in place on screen stays shown.
    const renderedText = container.textContent ?? ''
    let showAll = false
    if (countedKeyRef.current !== revealKey) {
      countedKeyRef.current = revealKey
      if (isSameAnswer(countedTextRef.current, text)) {
        showAll = true
      } else {
        const stillShown = Math.min(
          revealedLengthRef.current,
          measureSharedPrefix(renderedTextRef.current, renderedText),
        )
        revealedLengthRef.current = Math.max(getRevealedLength(revealKey), stillShown)
        completedForTextRef.current = null
      }
    }
    countedTextRef.current = text
    renderedTextRef.current = renderedText
    if (completedForTextRef.current === text) {
      // The key can change under finished text (a refetch rebuilds the
      // message's parts); carry the length so a later remount doesn't replay.
      advanceRevealedLength(revealKey, revealedLengthRef.current)
      return
    }

    const units = collectRevealUnits(container)
    const unitSpans = measureUnitSpans(units)
    const textLength = unitSpans[unitSpans.length - 1]?.end ?? 0
    const holders = collectHolders(container)
    const finish = () => {
      container.removeAttribute('data-chunk-reveal')
      for (const el of units) {
        el.classList.remove('chat-reveal-unit')
        el.style.removeProperty('--reveal-delay')
      }
      for (const el of holders) {
        el.classList.remove('chat-reveal-holder')
        el.style.removeProperty('--reveal-delay')
      }
      revealedLengthRef.current = textLength
      advanceRevealedLength(revealKey, textLength)
      completedForTextRef.current = text
    }

    if (showAll || !shouldAnimate || reducedMotion) {
      finish()
      return
    }

    // A unit was shown if it starts inside the watermark. Testing its end
    // instead would hide it again whenever streaming grows it in place (the
    // last word gaining characters, an open code fence, a table). Starts are
    // monotonic, so the shown units are a prefix.
    let alreadyRevealed = 0
    while (alreadyRevealed < units.length && unitSpans[alreadyRevealed].start < revealedLengthRef.current) {
      alreadyRevealed++
    }
    const delays = computeChunkDelays(units.length, alreadyRevealed)
    const freshDelays = new Map<HTMLElement, number>()
    units.forEach((el, i) => {
      if (i < alreadyRevealed) {
        el.classList.remove('chat-reveal-unit')
        el.style.removeProperty('--reveal-delay')
      } else {
        freshDelays.set(el, delays[i])
      }
    })
    // A holder hides only while every chunk under it is still fresh; its first
    // chunk (first in document order = smallest delay) sets when it appears.
    const hiddenHolderDelays = new Map<HTMLElement, number>()
    for (const holder of holders) {
      const firstChunk = holder.querySelector<HTMLElement>(`.${REVEAL_CHUNK_CLASS}`)
      if (firstChunk && freshDelays.has(firstChunk)) {
        hiddenHolderDelays.set(holder, freshDelays.get(firstChunk) ?? 0)
      }
    }
    // A hiding holder is display: none through its own delay, and a
    // display: none subtree runs no animations — descendants only start their
    // clocks when the holder shows. So everything under a hiding holder gets
    // its delay relative to that holder's, not to the reveal's start; the
    // absolute schedule (and the fallback timeout below) is unchanged.
    const nearestHiddenHolderDelay = (el: HTMLElement): number => {
      for (let parent = el.parentElement; parent && parent !== container; parent = parent.parentElement) {
        const holderDelay = hiddenHolderDelays.get(parent)
        if (holderDelay !== undefined) return holderDelay
      }
      return 0
    }
    for (const [el, delay] of freshDelays) {
      el.style.setProperty('--reveal-delay', `${Math.max(0, delay - nearestHiddenHolderDelay(el))}ms`)
      el.classList.add('chat-reveal-unit')
    }
    for (const [holder, delay] of hiddenHolderDelays) {
      holder.style.setProperty('--reveal-delay', `${Math.max(0, delay - nearestHiddenHolderDelay(holder))}ms`)
      holder.classList.add('chat-reveal-holder')
    }
    // This effect runs before paint, so units are never visible early.
    container.setAttribute('data-chunk-reveal', '')

    const animatingCount = units.length - alreadyRevealed
    if (animatingCount === 0) {
      finish()
      return
    }

    const unitIndexes = new Map(units.map((unit, i) => [unit, i]))
    // A unit shows the moment its animation starts (display flips from none
    // at progress > 0), so that is when it joins the watermark. Waiting for
    // the end would let text growth inside the 80ms animation hand the unit a
    // fresh delay and hide it again. Holder animations share the container
    // but carry their own keyframe name, so only units count.
    const handleAnimationStart = (e: AnimationEvent) => {
      if (e.animationName !== 'chat-chunk-reveal') return
      const index = unitIndexes.get(e.target as HTMLElement)
      if (index === undefined) return
      revealedLengthRef.current = Math.max(revealedLengthRef.current, unitSpans[index].end)
      advanceRevealedLength(revealKey, revealedLengthRef.current)
    }
    let endedCount = 0
    const handleAnimationEnd = (e: AnimationEvent) => {
      if (e.animationName !== 'chat-chunk-reveal') return
      endedCount += 1
      if (endedCount >= animatingCount) {
        finish()
      }
    }
    container.addEventListener('animationstart', handleAnimationStart)
    container.addEventListener('animationend', handleAnimationEnd)
    const maxDelay = delays[delays.length - 1] ?? 0
    const fallbackId = window.setTimeout(finish, maxDelay + CHUNK_REVEAL_DURATION_MS + REVEAL_FALLBACK_SLACK_MS)
    return () => {
      container.removeEventListener('animationstart', handleAnimationStart)
      container.removeEventListener('animationend', handleAnimationEnd)
      window.clearTimeout(fallbackId)
    }
  }, [text, shouldAnimate, reducedMotion, revealKey])

  if (!text) return null

  return (
    <MessageMarkdown
      ref={assignContainerRef}
      content={text}
      messageId={messageId}
      shouldAnimate={false}
      revealChunks={shouldAnimate}
      className="text-foreground mb-4"
    />
  )
})
